import { supabase } from '@/lib/supabase';
import type { Json } from '@/lib/database.types';
import { formulaField, type GridFormulaConfig } from '@/shared/formula/engine';
import { formulaReferences } from '@/shared/formula/language';
import { fromStored, type StoredFormula } from '@/shared/formula/stored';
import type { CellFormulaRuntime } from '@/shared/formula/cellRuntime';

/**
 * Formula results, written back to the row they belong to.
 *
 * A calculated cell used to exist only while its grid was open. That is enough
 * for the grid itself, and not enough for anyone who wants the values of one
 * column: evaluating a formula needs the rows it reads, so borrowing a list
 * from another document meant loading that document. Persisting the result
 * turns that into a single narrow read.
 *
 * What is stored is a cache. The grid keeps evaluating live and rewrites this
 * whenever rows, formulas or columns change, so a value written by an older
 * client repairs itself the next time that tab is opened.
 */
export interface ComputedRow {
  id: string;
  computed: Record<string, unknown>;
}

/** Every field of a tab whose value comes from a formula rather than from typing. */
export function formulaFieldsOf(config: GridFormulaConfig): string[] {
  return [
    ...config.columns.map((column) => formulaField(column.id)),
    ...Object.keys(config.overrides),
  ];
}

/**
 * Whether any formula in `config` reads `sheetId`. Editing a tab changes the
 * results of the tabs that reference it, which would otherwise stay stale until
 * someone opened them.
 */
export function referencesSheet(config: GridFormulaConfig, sheetId: string): boolean {
  const formulas: StoredFormula[] = [
    ...config.columns.map((column) => column.formula),
    ...Object.values(config.overrides),
  ];
  return formulas.some((formula) => {
    try {
      return formulaReferences(fromStored(formula)).some((reference) => reference.sheetId === sheetId);
    } catch {
      // An unreadable formula references nothing we can act on.
      return false;
    }
  });
}

/**
 * The results of one tab, ready to store. Rows the runtime cannot place (no
 * stable id) are skipped rather than written half-evaluated.
 */
export function computeSheetValues({
  runtime,
  sheetId,
  rows,
  fields,
}: {
  runtime: CellFormulaRuntime;
  sheetId: string;
  rows: readonly Record<string, unknown>[];
  fields: readonly string[];
}): ComputedRow[] {
  if (fields.length === 0) return [];

  const result: ComputedRow[] = [];
  for (const row of rows) {
    const id = typeof row.id === 'string' ? row.id : null;
    if (id === null) continue;

    const computed: Record<string, unknown> = {};
    for (const field of fields) {
      const value = runtime.valueOf({ sheetId, rowId: `id:${id}`, field });
      if (value.kind === 'number') computed[field] = value.n;
      else if (value.kind === 'text') computed[field] = value.s;
      else if (value.kind === 'error') computed[field] = null;
      else computed[field] = null;
    }
    result.push({ id, computed });
  }
  return result;
}

/**
 * Writes a recompute. The RPC updates only the rows whose value actually
 * changed, and `sheet_rows_write` is what decides whether any of it lands — a
 * reader-only visitor simply writes nothing.
 */
export async function saveSheetComputed(
  documentId: string,
  tabId: string,
  rows: readonly ComputedRow[],
): Promise<string | null> {
  if (rows.length === 0) return null;
  const { error } = await supabase.rpc('save_sheet_computed', {
    p_document_id: documentId,
    p_tab_id: tabId,
    p_rows: rows as unknown as Json,
  });
  return error?.message ?? null;
}

/**
 * The distinct values of one column, read directly. Only for the person setting
 * a link up: they can read the source, so this runs under their own access.
 * Everyone else reads the pushed snapshot instead.
 */
export async function loadColumnValues(
  documentId: string,
  tabId: string,
  field: string,
): Promise<string[]> {
  const { data, error } = await supabase.rpc('sheet_column_values', {
    p_document_id: documentId,
    p_tab_id: tabId,
    p_field: field,
  });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => row.value);
}

/**
 * Records that a column borrows its list from another column, and takes the
 * first snapshot. Whoever registers it must be able to read the source; every
 * later push is legitimate because this moment was.
 */
export async function registerLinkedList(
  documentId: string,
  tabId: string,
  field: string,
  source: { documentId: string; tabId: string; field: string },
): Promise<string | null> {
  const { error } = await supabase.rpc('register_linked_list', {
    p_document_id: documentId,
    p_tab_id: tabId,
    p_field: field,
    p_source_document_id: source.documentId,
    p_source_tab_id: source.tabId,
    p_source_field: source.field,
  });
  return error?.message ?? null;
}

/** Forgets a borrow and the snapshot it fed. */
export async function unregisterLinkedList(
  documentId: string,
  tabId: string,
  field: string,
): Promise<string | null> {
  const { error } = await supabase.rpc('unregister_linked_list', {
    p_document_id: documentId,
    p_tab_id: tabId,
    p_field: field,
  });
  return error?.message ?? null;
}

/**
 * Puts on record any borrow this tab declares but has not registered — a link
 * saved before registrations existed, or one whose registration was lost. The
 * registration is what remembers which source row each value came from, so
 * without it an edited value cannot follow into the cells that chose it.
 *
 * Registering requires format rights on this document and read on the source;
 * anyone else gets an error here, which is ignored: they are not the person who
 * repairs it.
 */
export async function ensureLinkedListRegistrations(
  documentId: string,
  tabId: string,
  links: readonly { field: string; source: { documentId: string; tabId: string; field: string } }[],
): Promise<void> {
  if (links.length === 0) return;

  const { data, error } = await supabase
    .from('linked_list_registrations')
    .select('field')
    .eq('document_id', documentId)
    .eq('tab_id', tabId);
  if (error) return;

  const registered = new Set((data ?? []).map((row) => row.field));
  await Promise.all(links
    .filter((link) => !registered.has(link.field))
    .map((link) => registerLinkedList(documentId, tabId, link.field, link.source)));
}

let kickTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Nudges the database to recompute the lists a write just flagged.
 *
 * The trigger on `sheet_rows` only marks what changed, so that a write stays a
 * write; something has to do the work. The cron sweep is the guarantee, and
 * this is what makes the everyday case immediate — debounced only enough to
 * coalesce a burst, because a paste is one intention however many statements it
 * takes. What gets recomputed is decided by the registrations, never by the
 * caller, so any signed-in user may ask for it.
 */
export function kickLinkedListRefresh(): void {
  if (kickTimer) clearTimeout(kickTimer);
  kickTimer = setTimeout(() => {
    kickTimer = null;
    void refreshDirtyLinkedLists();
  }, 250);
}

/**
 * The same work, right now. Used straight after storing formula results: those
 * results are what a borrowed list is made of, so waiting out a debounce here
 * only delays what the reader is waiting to see.
 */
export async function refreshDirtyLinkedLists(): Promise<void> {
  if (kickTimer) { clearTimeout(kickTimer); kickTimer = null; }
  void kickAutomations();
  await supabase.rpc('refresh_dirty_linked_lists', { p_limit: 200 });
}

/**
 * Runs the automations queued by the write that just landed (Apps Script's
 * onEdit), instead of leaving them to the Worker's next minute sweep. The
 * queue decides what runs, never this caller, and a failure here only means
 * the sweep picks it up.
 */
async function kickAutomations(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    await fetch('/api/automations/run', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  } catch {
    // The minute sweep is the fallback.
  }
}

/**
 * Brings this document's borrowed lists up to date, for a caller who holds both
 * accesses — read on each source, format on the borrowing column. Anyone else
 * refreshes nothing, which is the point: moving values between two documents
 * takes rights over both.
 */
export async function refreshLinkedLists(documentId: string): Promise<void> {
  await supabase.rpc('refresh_linked_lists', { p_document_id: documentId });
}

/** The lists pushed into one document, keyed `tabId|field`. */
export async function loadPushedLists(documentId: string): Promise<Map<string, string[]>> {
  const { data, error } = await supabase
    .from('linked_list_values')
    .select('tab_id, field, values')
    .eq('document_id', documentId);
  if (error) throw new Error(error.message);

  const result = new Map<string, string[]>();
  for (const row of data ?? []) {
    const values = Array.isArray(row.values)
      ? row.values.flatMap((value) => (typeof value === 'string' ? [value] : []))
      : [];
    result.set(`${row.tab_id}|${row.field}`, values);
  }
  return result;
}
